import React, { useState, useEffect } from 'react';
import { Popover } from '@headlessui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchCategories } from '../../services/apiService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { pickCategoryName } from '../../utils/categoryUtils';
import SearchBar from './SearchBar';
import NavSearchBar from './NavSearchBar';
import Sidebar from './Sidebar';
import useCartCount from '../../hooks/useCartCount';
import {
  HiOutlineHome,
  HiOutlineSearch,
  HiOutlineShoppingCart,
  HiOutlineHeart,
  HiOutlineUser,
  HiOutlineMenu,
  HiOutlineQuestionMarkCircle,
  HiOutlineBookOpen,
  HiOutlineBriefcase,
  HiOutlineShieldCheck,
  HiOutlineInformationCircle,
  HiOutlineClipboardList,
  HiOutlineChevronDown,
  HiOutlineGlobeAlt,
  HiOutlineDocumentText,
  HiCheck,
} from 'react-icons/hi';
import { LuShoppingBag, LuSparkles, LuMessageCircleQuestion } from 'react-icons/lu';

// Single source of truth for the language switcher — rendered by both the
// desktop popover and the mobile top-bar pill so the two can't drift apart.
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'it', label: 'Italiano' },
];


const solutions = [
];


const callsToAction = [
];

const company = (t) => [
  {
    name: t('nav.company.aboutUs'),
    href: '/aboutus',
    icon: <HiOutlineQuestionMarkCircle className="h-5 w-5" />
  },
  {
    name: t('nav.company.services'),
    href: '/services',
    icon: <HiOutlineBookOpen className="h-5 w-5" />
  },
  {
    name: t('nav.company.career'),
    href: '/jobs',
    icon: <HiOutlineBriefcase className="h-5 w-5" />
  },
  {
    name: t('nav.company.privacy'),
    href: '/privacypolicy',
    icon: <HiOutlineShieldCheck className="h-5 w-5" />
  },
  {
    name: t('nav.company.terms'),
    href: '/termsconditions',
    icon: <HiOutlineDocumentText className="h-5 w-5" />
  },
];



const resources = (t) => [
  {
    name: t('nav.resources.healthArticles'),
    href: '/blogs',
    icon: <HiOutlineInformationCircle className="h-5 w-5" />,
  },
  {
    name: t('nav.resources.faqs'),
    href: '/faqs',
    icon: <HiOutlineClipboardList className="h-5 w-5" />,
  },
];

const blogPosts = [
  {
    title: 'ENIGMA SCHLAF',
    href: '/enigma-schlaf',
    category: { name: 'Dreirosen Apotheke'},
    description:
      'Schlafmangel macht, schreibt Peter Spork, nicht nur krank, sondern auch dumm. Denn eine der Hauptaufgaben des Schlafes scheint darin zu bestehen, am Tag Gelerntes dauerhaft zu speichern...',
    date: 'Mar 10, 2020',
    datetime: '2020-03-10',
    imageUrl: 'images/sleepimg3.jpg',
  },
  {
    title: 'Wie komplett ist Ihre Reiseapotheke?',
    href: 'Ferien-konvertiert',
    category: { name: 'Dreirosen Apotheke'},
    description:
      'Bitte prüfen Sie Ihre Reiseapotheke anhand unserer Checkliste. Darin finden Sie eine Auswahl an Produkten, welche wir Ihnen für Ihre Reise empfehlen...',
    date: 'Feb 12, 2020',
    datetime: '2020-02-12',
    imageUrl: 'images/travelimg1.jpg',
  },
];



// Focus ring for controls on the red bar: white, offset against the red, so it
// stays visible (the old red-200 ring vanished into the background).
const RING = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav';

// Cart item count on the cart icon. Keyed by the count so it pops each time
// it changes; hidden at 0; capped at 99+. The ring is the colour of whatever
// sits behind it, so the badge reads as cut out of the icon.
const CART_BADGE_TONES = {
  onRed: 'bg-nav-ink text-nav ring-nav landing:bg-petal landing:text-white',
  onSand: 'bg-brand text-white ring-[#F6F3F0]',
  onActive: 'bg-white text-brand ring-brand',
};
const CartBadge = ({ count, tone }) =>
  count > 0 ? (
    <span
      key={count}
      aria-hidden="true"
      className={`cart-badge-pop pointer-events-none absolute -right-2 -top-1.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-extrabold leading-none tabular-nums ring-2 ${CART_BADGE_TONES[tone]}`}
    >
      <style>{`
        @keyframes cart-badge-pop { 0% { transform: scale(0.4); } 60% { transform: scale(1.18); } 100% { transform: scale(1); } }
        .cart-badge-pop { animation: cart-badge-pop 0.35s cubic-bezier(0.22,1,0.36,1); }
        @media (prefers-reduced-motion: reduce) { .cart-badge-pop { animation: none; } }
      `}</style>
      {count > 99 ? '99+' : count}
    </span>
  ) : null;

    export default function Nav() {
      const { isLoggedIn } = useAuth();
      const location = useLocation();
      const navigate = useNavigate();
      const [showSearch, setShowSearch] = useState(false);
      const [categories, setCategories] = useState([]);
      const { language, changeLanguage, t } = useLanguage();
      const cartCount = useCartCount();
      const cartLabel = cartCount > 0 ? `${t('nav.buttons.viewCart')} (${cartCount})` : t('nav.buttons.viewCart');

      // Publish the bar's live height as --nav-h, so what sticks below it
      // (TopCategoriesBar, the sticky side panels) can sit right under it.
      // Home renders two navs (desktop / mobile trees); the hidden one
      // measures 0, so take the tallest.
      useEffect(() => {
        const bars = () => document.querySelectorAll('[data-site-nav]');
        const update = () => {
          let h = 0;
          bars().forEach((el) => { h = Math.max(h, el.offsetHeight); });
          document.documentElement.style.setProperty('--nav-h', `${h}px`);
        };
        update();
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (ro) bars().forEach((el) => ro.observe(el));
        window.addEventListener('resize', update, { passive: true });
        return () => {
          if (ro) ro.disconnect();
          window.removeEventListener('resize', update);
        };
      }, []);

      useEffect(() => {
        const fetchData = async () => {
          // Fetch categories
          try {
            const response = await fetchCategories();
            setCategories(response.data);
          } catch (error) {
            // Error handled silently - categories will be empty
          }
        };

        fetchData();
      }, []);



      const handleCategoryClick = (category) => {
        const cleanCategoryId = category.category.en.trim();
        sessionStorage.setItem('germanName', category.category.de);
        navigate(`/categories/${cleanCategoryId}/products`);
      };



      return (
        <>
          {/* Skip to main content link for keyboard navigation */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[10000] focus:px-4 focus:py-2 focus:bg-brand focus:text-white focus:rounded-lg focus:ring-2 focus:ring-offset-2 focus:ring-red-300"
          >
            {t('nav.skipToContent')}
          </a>
          
          {/* Sticky on every screen: the banner above scrolls away, the bar
              stays (and on large screens TopCategoriesBar sticks under it). */}
          <Popover data-site-nav="" className="sticky top-[env(safe-area-inset-top,0px)] z-[9999] bg-nav pt-1 custom:top-0 custom:shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] landing:bg-nav/80 landing:backdrop-blur-xl landing:backdrop-saturate-150 landing:shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_1px_0_rgb(var(--brand)/0.08)]" role="banner">
            <div className="relative z-20">
              <div className="mx-auto flex max-w-[1800px] items-center justify-between gap-4 px-2 sm:py-0 custom:gap-5 custom:px-5 xl:gap-8 xl:px-8 2xl:gap-12 2xl:px-10">
                {/* Left side - Logo */}
                <div className="flex flex-shrink-0 items-center">
                  <Link
                    to="/"
                    className={`group flex rounded-[1.25rem] ${RING}`}
                    aria-label={t('nav.homepage')}
                  >
                    <span className="sr-only">{t('nav.homepage')}</span>
                    {/* The mark is full colour (a red rose, red lettering), so
                        on the red bar it sits on its own white badge rather
                        than being knocked out to white and losing the green
                        pharmacy cross. On the landing page's light glass bar
                        it needs no badge: the transparent SVG sits directly
                        on the bar. */}
                    <span className="my-1 flex items-center justify-center overflow-hidden rounded-[1.1rem] bg-white p-1 shadow-[0_10px_26px_-12px_rgb(var(--brand-shadow-deep)/0.55)] ring-1 ring-black/5 transition-transform duration-300 group-hover:-rotate-2 group-hover:scale-[1.03] lg:rounded-[1.4rem] lg:p-1.5 landing:overflow-visible landing:bg-transparent landing:p-0 landing:shadow-none landing:ring-0 lg:landing:p-0">
                      <img
                        alt="Dreirosen Apotheke Logo"
                        src="/images/resized_newlogo.webp"
                        loading="eager"
                        width="120"
                        height="96"
                        className="h-11 w-auto max-w-none rounded-[0.8rem] sm:h-12 lg:h-[4.5rem] lg:rounded-[1rem] landing:hidden"
                      />
                      <img
                        alt="Dreirosen Apotheke Logo"
                        src="/images/dreirosen-logo.svg"
                        loading="eager"
                        width="96"
                        height="96"
                        className="hidden h-12 w-12 drop-shadow-[0_6px_14px_rgb(var(--brand)/0.18)] sm:h-14 sm:w-14 lg:h-[4.75rem] lg:w-[4.75rem] landing:block"
                      />
                    </span>
                  </Link>
                </div>

                {/* Mobile right-side actions: primary "discover products" CTA
                    + menu button. The CTA lives here (instead of the hero) on
                    mobile to keep the hero uncluttered. It's lg:hidden so it
                    only shows where the mobile hero — which dropped its own
                    CTA — is active; the desktop hero keeps its CTA at ≥1024px. */}
                <div className="custom:hidden flex items-center gap-2.5 ml-auto">
                  <Link
                    to="/products"
                    className="lg:hidden group inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-brand landing:bg-brand landing:text-white landing:hover:bg-brand-hover shadow-[0_8px_20px_-10px_rgb(var(--brand-shadow-deep)/0.6)] transition-all duration-200 hover:bg-red-50 active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav whitespace-nowrap"
                    aria-label={t('hero.ctaDiscover')}
                  >
                    <LuShoppingBag className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" strokeWidth={1.9} aria-hidden="true" />
                    {t('hero.ctaDiscover')}
                  </Link>

                  {/* Mobile language switcher — the desktop popover below is
                      hidden under `custom:`, so without this the language
                      cannot be changed on phones/tablets at all. */}
                  <Popover className="relative">
                    <Popover.Button
                      className="group inline-flex items-center gap-1.5 h-10 pl-2.5 pr-3 rounded-full border border-nav-ink/25 bg-nav-ink/10 text-nav-ink transition-all duration-200 hover:bg-nav-ink/20 active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav data-[open]:bg-white data-[open]:text-brand landing:border-brand/25 landing:bg-brand/10 landing:text-brand landing:hover:bg-brand/15 landing:data-[headlessui-state~=open]:bg-brand landing:data-[headlessui-state~=open]:text-white"
                      aria-label={t('nav.selectLanguage')}
                    >
                      <HiOutlineGlobeAlt className="h-[18px] w-[18px] transition-transform duration-200 group-hover:rotate-12" strokeWidth={2} aria-hidden="true" />
                      <span className="text-[13px] font-bold uppercase tracking-wide leading-none">{language}</span>
                    </Popover.Button>

                    <Popover.Panel
                      transition
                      className="absolute right-0 top-full mt-2.5 w-44 origin-top-right overflow-hidden rounded-2xl bg-white shadow-2xl shadow-gray-900/10 ring-1 ring-gray-900/5 z-[60] transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
                    >
                      {({ close }) => (
                        <div role="presentation" onClick={(e) => e.stopPropagation()}>
                          {/* Brand accent strip, matching the desktop mega-menu */}
                          <div className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-red-600 landing:from-brand landing:via-brand-light landing:to-brand" aria-hidden="true" />
                          <div className="p-1.5">
                            {LANGUAGES.map(({ code, label }) => {
                              const isActive = language === code;
                              return (
                                <button
                                  key={code}
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    changeLanguage(code);
                                    setTimeout(() => close(), 0);
                                  }}
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-[14px] transition-colors duration-150 ${
                                    isActive
                                      ? 'bg-red-50 text-red-700 font-semibold landing:bg-brand-tint landing:text-brand-deep'
                                      : 'text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100'
                                  }`}
                                  aria-current={isActive ? 'true' : undefined}
                                >
                                  <span className={`text-[11px] font-bold uppercase tracking-wider w-6 ${isActive ? 'text-red-500 landing:text-brand' : 'text-gray-400'}`}>
                                    {code}
                                  </span>
                                  <span className="flex-1">{label}</span>
                                  {isActive && <HiCheck className="h-4 w-4 text-red-500 landing:text-brand" aria-hidden="true" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </Popover.Panel>
                  </Popover>

                  <Popover.Button
                    className="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-nav-ink transition-all duration-200 hover:bg-nav-ink/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav"
                    aria-label={t('nav.buttons.openMainMenu')}
                  >
                    <span className="sr-only">{t('nav.buttons.openMainMenu')}</span>
                    <HiOutlineMenu className="h-6 w-6 transition-all duration-200 group-hover:scale-110" style={{ strokeWidth: 2.2 }} aria-hidden="true" />
                  </Popover.Button>
                </div>

              {/* Mobile primary actions (Search/Cart/Profile/Shop/Home) now
                  live in the fixed bottom tab bar below — keeps the top bar
                  clean with just the logo + menu. */}

              {/* Desktop navigation - Centered */}
              {/* Everything right of the logo shares one row that can shrink: the
                  links keep their size, the search field takes whatever is left
                  (see NavSearchBar), and link labels collapse to icons below
                  xl. Nothing here has a fixed width that could push the row
                  past the viewport at 100% zoom on a laptop. */}
              <div className="hidden min-w-0 custom:flex custom:flex-1 custom:items-center">
                <div className="flex w-full min-w-0 items-center gap-1 xl:gap-3 2xl:gap-6 min-[1800px]:gap-10">
                  <Link 
                    to="/" 
                    className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav ${
                      location.pathname === '/'
                        ? 'bg-white text-brand shadow-[0_8px_20px_-10px_rgb(var(--brand-shadow-deep)/0.6)] landing:bg-brand landing:text-white'
                        : 'text-nav-ink/80 hover:bg-nav-ink/15 hover:text-nav-ink'
                    }`}
                    aria-label={t('nav.homepage')}
                    aria-current={location.pathname === '/' ? 'page' : undefined}
                  >
                    <span className="sr-only">{t('nav.homepage')}</span>
                    <HiOutlineHome className="h-6 w-6" aria-hidden="true" />
                  </Link>

                  <Popover>
                    <Popover.Button 
                      className="group inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-[15px] font-medium text-nav-ink/85 xl:gap-2 xl:px-3.5 transition-all duration-200 hover:bg-nav-ink/15 hover:text-nav-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav data-[open]:bg-white data-[open]:text-brand"
                      aria-label={t('nav.buttons.openMainMenu')}
                      aria-expanded="false"
                      aria-haspopup="true"
                    >
                      <span>{t('nav.menu')}</span>
                      <HiOutlineChevronDown className="h-4 w-4 transition-transform duration-300 group-data-[open]:rotate-180" aria-hidden="true" />
                    </Popover.Button>
                    <Popover.Panel
                      transition
                      className="absolute inset-x-0 top-full z-10 hidden px-[clamp(0.75rem,1.6vw,2rem)] pt-3 transition data-[closed]:-translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in md:block"
                      role="menu"
                      aria-label={t('nav.buttons.openMainMenu')}
                    >
                      {({ close }) => (
                        /* The site's sand surface rather than red: a floating
                           panel in the page gutter, with the red kept for small
                           accents (dots, hovered text, the active page). */
                        <div
                          className="relative mx-auto max-w-[1800px] overflow-hidden rounded-[2rem] p-3 ring-1 ring-black/[0.05] shadow-[0_40px_80px_-40px_rgba(17,24,39,0.45)]"
                          style={{ background: 'linear-gradient(135deg, rgb(var(--brand-tint)) 0%, #F6F3F0 40%, #F6F3F0 100%)' }}
                        >
                          <div
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-0"
                            style={{ backgroundImage: 'radial-gradient(rgba(17,24,39,0.07) 1px, transparent 1px)', backgroundSize: '18px 18px' }}
                          />

                          <div className="relative grid grid-cols-12 gap-3">
                            {/* ---- Categories ---- */}
                            <div className="col-span-8 p-5 xl:p-7">
                              <div className="mb-5 flex items-center justify-between gap-4">
                                <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
                                  {t('nav.sections.categories')}
                                </h3>
                                <Link
                                  to="/products"
                                  onClick={() => close()}
                                  className="group inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-black/[0.06] transition-colors duration-200 hover:text-brand"
                                >
                                  {t('nav.sections.showAllCategories')}
                                  <span aria-hidden="true" className="transition-transform duration-200 group-hover:translate-x-0.5">&rarr;</span>
                                </Link>
                              </div>
                              <div className="grid grid-cols-3 gap-2 xl:grid-cols-4">
                                {categories.slice(0, 16).map((category) => {
                                  const name = pickCategoryName(category.category, language);
                                  return (
                                    <button
                                      key={category._id}
                                      type="button"
                                      onClick={() => { handleCategoryClick(category); close(); }}
                                      className="group flex min-w-0 items-center gap-2.5 rounded-2xl bg-white/70 px-3.5 py-3 text-left text-sm font-semibold text-gray-800 ring-1 ring-black/[0.04] transition-all duration-200 hover:-translate-y-0.5 hover:bg-white hover:text-brand hover:shadow-[0_12px_26px_-18px_rgb(var(--brand)/0.6)] focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                                      title={name}
                                      role="menuitem"
                                      aria-label={`Kategorie ${name} anzeigen`}
                                    >
                                      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand/40 transition-colors duration-200 group-hover:bg-brand" aria-hidden="true" />
                                      <span className="truncate">{name}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* ---- Company + resources, on a white card ---- */}
                            <nav className="col-span-4 grid grid-cols-1 gap-6 rounded-[1.5rem] bg-white p-5 ring-1 ring-black/[0.04] xl:grid-cols-2 xl:p-6">
                              {[
                                { title: t('nav.sections.company'), items: company(t) },
                                { title: t('nav.sections.resources'), items: resources(t) },
                              ].map(({ title, items }) => (
                                <div key={title}>
                                  <h3 className="mb-3 px-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">{title}</h3>
                                  <ul className="space-y-0.5">
                                    {items.map((item) => {
                                      const active = location.pathname === item.href;
                                      return (
                                        <li key={item.name}>
                                          <Link
                                            to={item.href}
                                            onClick={() => close()}
                                            role="menuitem"
                                            aria-current={active ? 'page' : undefined}
                                            className={`group flex items-center gap-3 rounded-xl px-2 py-2 text-sm font-medium transition-colors duration-200 ${
                                              active ? 'bg-[#F6F3F0] text-brand' : 'text-gray-700 hover:bg-[#F6F3F0] hover:text-brand'
                                            }`}
                                          >
                                            <span
                                              className={`inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200 [&>svg]:h-4 [&>svg]:w-4 ${
                                                active ? 'bg-white text-brand ring-1 ring-black/[0.05]' : 'bg-[#F6F3F0] text-gray-500 group-hover:bg-white group-hover:text-brand'
                                              }`}
                                              aria-hidden="true"
                                            >
                                              {item.icon}
                                            </span>
                                            <span className="min-w-0 flex-1 truncate">{item.name}</span>
                                            <span className="text-gray-300 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-brand" aria-hidden="true">&rarr;</span>
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ))}
                            </nav>
                          </div>
                        </div>
                      )}
                    </Popover.Panel>
                  </Popover>
                  <Link
                    to="/products"
                    className={`inline-flex flex-shrink-0 items-center gap-2 rounded-full px-3 py-2 text-[15px] transition-all duration-200 xl:px-3.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav ${
                      location.pathname === '/products'
                        ? 'bg-white font-semibold text-brand shadow-[0_8px_20px_-10px_rgb(var(--brand-shadow-deep)/0.6)] landing:bg-brand landing:text-white'
                        : 'font-medium text-nav-ink/85 hover:bg-nav-ink/15 hover:text-nav-ink'
                    }`}
                    aria-current={location.pathname === '/products' ? 'page' : undefined}
                    aria-label={t('nav.onlineShop')}
                    title={t('nav.onlineShop')}
                  >
                    <LuShoppingBag className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                    <span className="hidden xl:inline">{t('nav.onlineShop')}</span>
                  </Link>
                  <Link
                    to="/aboutus"
                    className={`inline-flex flex-shrink-0 items-center gap-2 rounded-full px-3 py-2 text-[15px] transition-all duration-200 xl:px-3.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav ${
                      location.pathname === '/aboutus'
                        ? 'bg-white font-semibold text-brand shadow-[0_8px_20px_-10px_rgb(var(--brand-shadow-deep)/0.6)] landing:bg-brand landing:text-white'
                        : 'font-medium text-nav-ink/85 hover:bg-nav-ink/15 hover:text-nav-ink'
                    }`}
                    aria-current={location.pathname === '/aboutus' ? 'page' : undefined}
                    aria-label={t('nav.aboutUs')}
                    title={t('nav.aboutUs')}
                  >
                    <LuSparkles className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                    <span className="hidden xl:inline">{t('nav.aboutUs')}</span>
                  </Link>

                  {/* Labelled "FAQ" rather than the spelled-out question, so
                      the row leaves room for the search field. The long form
                      is still what the Resources menu and footer use — see
                      nav.resources.faqs. */}
                  <Link
                    to="/faqs"
                    className={`inline-flex flex-shrink-0 items-center gap-2 rounded-full px-3 py-2 text-[15px] transition-all duration-200 xl:px-3.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav ${
                      location.pathname === '/faqs'
                        ? 'bg-white font-semibold text-brand shadow-[0_8px_20px_-10px_rgb(var(--brand-shadow-deep)/0.6)] landing:bg-brand landing:text-white'
                        : 'font-medium text-nav-ink/85 hover:bg-nav-ink/15 hover:text-nav-ink'
                    }`}
                    aria-current={location.pathname === '/faqs' ? 'page' : undefined}
                    aria-label={t('nav.resources.faqs')}
                    title={t('nav.resources.faqs')}
                  >
                    <LuMessageCircleQuestion className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
                    <span className="hidden xl:inline">{t('nav.faqs')}</span>
                  </Link>

                  {/* User actions */}
                  <div className="ml-2 flex min-w-0 flex-1 items-center justify-end gap-1.5 xl:ml-4 xl:gap-2.5 2xl:ml-8 2xl:gap-3.5 min-[1800px]:ml-12">
                    {/* Search — an always-visible field rather than an icon
                        that opens the full-screen panel. The panel is still
                        what mobile uses (and the bottom tab bar's Suche), so
                        this is `custom:` and up only and the two never both
                        appear. */}
                    <NavSearchBar />

                    {/* Language Selector */}
                    <Popover className="relative">
                      {/* A pill with a globe, the current code and a chevron —
                          bare letters on the red bar were easy to miss. */}
                      <Popover.Button
                        className="hidden custom:flex group relative h-10 flex-shrink-0 items-center gap-1.5 rounded-full bg-nav-ink/15 pl-2.5 pr-2 text-nav-ink ring-1 ring-inset ring-nav-ink/25 transition-all duration-200 hover:bg-nav-ink/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav data-[open]:bg-white data-[open]:text-brand data-[open]:ring-transparent landing:bg-brand/10 landing:text-brand landing:ring-brand/25 landing:hover:bg-brand/15 landing:data-[headlessui-state~=open]:bg-brand landing:data-[headlessui-state~=open]:text-white"
                        aria-label={t('nav.selectLanguage')}
                        title={t('nav.selectLanguage')}
                      >
                        <HiOutlineGlobeAlt className="h-[18px] w-[18px] transition-transform duration-300 group-hover:rotate-12" strokeWidth={2} aria-hidden="true" />
                        <span className="text-[13px] font-bold uppercase leading-none tracking-wide">{language}</span>
                        <HiOutlineChevronDown className="h-3.5 w-3.5 opacity-80 transition-transform duration-200 group-data-[open]:rotate-180" aria-hidden="true" />
                      </Popover.Button>
                      <Popover.Panel className="absolute right-0 top-full mt-2.5 w-48 overflow-hidden rounded-2xl bg-white shadow-2xl shadow-gray-900/10 ring-1 ring-gray-900/5 z-50">
                        {({ close }) => (
                          <div role="presentation" onClick={(e) => e.stopPropagation()}>
                            <div className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-red-600 landing:from-brand landing:via-brand-light landing:to-brand" aria-hidden="true" />
                            <div className="p-1.5">
                              {LANGUAGES.map(({ code, label }) => {
                                const isActive = language === code;
                                return (
                                  <button
                                    key={code}
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      changeLanguage(code);
                                      setTimeout(() => close(), 0);
                                    }}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm transition-colors duration-150 ${
                                      isActive
                                        ? 'bg-red-50 text-red-700 font-semibold landing:bg-brand-tint landing:text-brand-deep'
                                        : 'text-gray-700 font-medium hover:bg-gray-50'
                                    }`}
                                    aria-current={isActive ? 'true' : undefined}
                                  >
                                    <span className={`text-[11px] font-bold uppercase tracking-wider w-6 ${isActive ? 'text-red-500 landing:text-brand' : 'text-gray-400'}`}>
                                      {code}
                                    </span>
                                    <span className="flex-1">{label}</span>
                                    {isActive && <HiCheck className="h-4 w-4 text-red-500 landing:text-brand" aria-hidden="true" />}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </Popover.Panel>
                    </Popover>

                    {/* Cart Button for Desktop */}
                    <Link
                      to="/cartpage"
                      className="hidden custom:flex group relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-nav-ink/85 transition-all duration-200 hover:bg-nav-ink/15 hover:text-nav-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav"
                      aria-label={cartLabel}
                    >
                      <HiOutlineShoppingCart className="h-6 w-6 transition-transform duration-200 group-hover:scale-110" style={{ strokeWidth: 2.2 }} aria-hidden="true" />
                      <CartBadge count={cartCount} tone="onRed" />
                    </Link>

                    {/* Favorites Button for Desktop - Only show when logged in */}
                    {isLoggedIn && (
                      <Link
                        to="/userfavoritespage"
                        className="hidden custom:flex group relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-nav-ink/85 transition-all duration-200 hover:bg-nav-ink/15 hover:text-nav-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav"
                        aria-label={t('nav.buttons.viewFavorites')}
                      >
                        <HiOutlineHeart className="h-6 w-6 transition-transform duration-200 group-hover:scale-110" style={{ strokeWidth: 2.2 }} aria-hidden="true" />
                            </Link>
                    )}

                    {!isLoggedIn ? (
                      <Link
                        to="/loginpage"
                        // Solid white on the red bar: the one filled pill in
                        // the row, so it reads as the account action without
                        // competing with the search field beside it.
                        className="group inline-flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-white px-4 py-2.5 text-sm font-semibold text-brand xl:px-6 shadow-[0_10px_24px_-12px_rgb(var(--brand-shadow-deep)/0.65)] transition-all duration-200 hover:-translate-y-px hover:bg-red-50 landing:bg-brand landing:text-white landing:hover:bg-brand-hover hover:shadow-[0_14px_28px_-12px_rgb(var(--brand-shadow-deep)/0.7)] focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav"
                      >
                        <HiOutlineUser className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
                        {t('nav.login')}
                      </Link>
                    ) : (
                      <Link
                        to="/profilepage"
                        className="hidden custom:flex group relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-nav-ink/85 transition-all duration-200 hover:bg-nav-ink/15 hover:text-nav-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav"
                      >
                        <HiOutlineUser className="h-6 w-6 transition-transform duration-200 group-hover:scale-110" style={{ strokeWidth: 2.2 }} aria-hidden="true" />
                      </Link>
                    )}

                    {/* Search Button for Mobile */}
                    <button
                      onClick={() => setShowSearch(!showSearch)}
                      className="custom:hidden group relative inline-flex h-10 w-10 items-center justify-center rounded-full text-nav-ink/85 transition-all duration-200 hover:bg-nav-ink/15 hover:text-nav-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-nav-ink/70 focus-visible:ring-offset-2 focus-visible:ring-offset-nav"
                    >
                      <HiOutlineSearch className="h-6 w-6 transition-transform duration-200 group-hover:scale-110" style={{ strokeWidth: 2.2 }} aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Search Bar - For both mobile and desktop */}
            {showSearch && (
              <div
                className="fixed inset-0 z-50"
                role="presentation"
                onKeyDown={(e) => { if (e.key === 'Escape') setShowSearch(false); }}
              >
                {/* Backdrop is a real button so dismissing works by click,
                    Enter/Space and Escape — it used to be a div carrying
                    role="dialog", which put the dialog semantics on the
                    scrim instead of on the search panel. */}
                <button
                  type="button"
                  aria-label={t('nav.buttons.openSearch')}
                  onClick={() => setShowSearch(false)}
                  className="absolute inset-0 h-full w-full cursor-default bg-black/50 backdrop-blur-sm"
                />
                <div
                  className="absolute left-1/2 top-24 w-[90%] max-w-md -translate-x-1/2"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Suchfeld"
                  id="search-modal"
                >
                  <SearchBar onClose={() => setShowSearch(false)} />
                </div>
              </div>
            )}
          </div>
      
      {/* Sidebar component */}
      <Sidebar solutions={solutions} callsToAction={callsToAction} company={company(t)} resources={resources(t)} />
    </Popover>

    {/* ===== Mobile bottom tab bar (hidden at custom: 1057px+) =====
        Frosted sand, like the rest of the site's surfaces: rounded top, a
        hairline edge and a soft lift. The active tab gets the one red touch —
        a red pill behind its icon — the rest stay quiet grey. */}
    <nav
      data-mobile-tabbar=""
      className="custom:hidden fixed bottom-0 inset-x-0 z-[9999] rounded-t-[1.75rem] bg-[#F6F3F0] ring-1 ring-black/[0.06] shadow-[0_-12px_32px_-14px_rgba(17,24,39,0.28)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      aria-label={t('nav.menu')}
    >
      {/* The same sand continued below the bar: iOS Safari's toolbar and the
          bottom bounce can open a strip under a bottom-0 bar, which showed the
          page through as a gap. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-full h-[50vh] bg-[#F6F3F0]" />
      <div className="flex items-stretch justify-around px-1.5 pt-1">
        {[
          { key: 'home', to: '/', label: t('nav.bottom.home') || 'Start', Icon: HiOutlineHome, active: location.pathname === '/' },
          { key: 'shop', to: '/products', label: t('nav.bottom.shop') || 'Shop', Icon: LuShoppingBag, active: location.pathname.startsWith('/products') || location.pathname.startsWith('/categories') },
          { key: 'search', label: t('nav.bottom.search') || 'Suche', Icon: HiOutlineSearch, active: showSearch, onClick: () => setShowSearch(true) },
          { key: 'cart', to: '/cartpage', label: t('nav.bottom.cart') || 'Korb', Icon: HiOutlineShoppingCart, active: location.pathname === '/cartpage', badge: cartCount },
          { key: 'account', to: isLoggedIn ? '/profilepage' : '/loginpage', label: t('nav.bottom.account') || 'Konto', Icon: HiOutlineUser, active: location.pathname === '/profilepage' || location.pathname === '/loginpage' },
        ].map(({ key, to, label, Icon, active, onClick, badge }) => {
          const inner = (
            <>
              <span
                className={`flex h-8 w-14 items-center justify-center rounded-full transition-all duration-300 ease-out ${
                  active
                    ? 'bg-brand text-white shadow-[0_8px_18px_-8px_rgb(var(--brand)/0.9)]'
                    : 'text-gray-500 group-hover:bg-white group-active:scale-90'
                }`}
              >
                <span className="relative">
                  <Icon className="h-[21px] w-[21px]" style={{ strokeWidth: 2 }} aria-hidden="true" />
                  {badge != null && <CartBadge count={badge} tone={active ? 'onActive' : 'onSand'} />}
                </span>
              </span>
              <span className={`mt-1 text-[10px] tracking-[0.01em] transition-colors duration-200 ${active ? 'font-bold text-brand' : 'font-medium text-gray-500'}`}>{label}</span>
            </>
          );
          const cls = 'group flex flex-1 flex-col items-center pb-1.5 pt-1.5 focus:outline-none focus-visible:[&>span:first-child]:ring-2 focus-visible:[&>span:first-child]:ring-red-300';
          return onClick ? (
            <button key={key} type="button" onClick={onClick} className={cls} aria-label={label}>
              {inner}
            </button>
          ) : (
            <Link key={key} to={to} className={cls} aria-current={active ? 'page' : undefined} aria-label={badge > 0 ? `${label} (${badge})` : label}>
              {inner}
            </Link>
          );
        })}
      </div>
    </nav>
    </>
  );
}
